import type { MarketCode } from './markets.js';

// Scope flag indicates the level of data available for an entity, driven by
// the source market's data policy. The flag is used by the UI to render
// "honest scope" — every panel checks the flag and either renders fully,
// renders with a degraded state, or surfaces a "data not available" CTA.
export const SCOPE_FLAGS = [
  'full',
  'scoped_ixbrl_only',
  'scoped_financial_only',
  'screener_only',
] as const;
export type ScopeFlag = (typeof SCOPE_FLAGS)[number];

// Default scope flag per market at launch.
// Per-entity flags can override (e.g., a Swedish iXBRL-filing entity is 'full'
// for financial data even though Sweden's default is 'scoped_ixbrl_only').
export const MARKET_DEFAULT_SCOPE: Record<MarketCode, ScopeFlag> = {
  EE: 'full',
  DK: 'full',
  NO: 'full',
  BE: 'full',
  SE: 'scoped_ixbrl_only',
  NL: 'scoped_financial_only',
  // Latvia: RE-ENTERED as the 27th market (2026-07-10, ex-0006 purge) —
  // UR gada pārskati reported spine + LM places; was screener_only.
  LV: 'scoped_financial_only',
  // UK is full at the market level — Companies House baseline covers all
  // UK companies. The brief's 11 UK-specific data-profile tags (the
  // `uk-full-nhsbsa`, `uk-private-dental`, etc. flavours) are tracked on a
  // separate `data_profile` column at the entity level, not in this enum.
  GB: 'full',
  // France: identity is full via SIRENE; a significant share of SME
  // healthcare entities elect accounts confidentiality (~50-70% of
  // dental/GP/specialty SMEs per france-market-brief.md §2.2), so the
  // financial layer is genuinely partial. The operating-footprint
  // workaround (FINESS + RPPS + DAMIR) compensates; per-entity
  // scope_flag is overridden on entities with non-confidential filings.
  FR: 'scoped_financial_only',
  // Wave-1 additions (registry-served 2026-07-09):
  // Germany: Handelsregister backbone covers the incorporated universe.
  DE: 'full',
  // Finland: PRH digital filings are voluntary until the FY2026 mandate —
  // the financial layer is iXBRL-filers-only (964 of 17,665 at first serve).
  FI: 'scoped_ixbrl_only',
  // Romania: MF bulk gives whole-population reported turnover (86.9%
  // reported at first serve) — full at the market level.
  RO: 'full',
  // Wave-2 EUR additions (registry-served 2026-07-09), capacity-anchored:
  // Italy: no free company financials — revenue modelled from capacity except
  // sparse RUNTS/ANAC actuals (283 of 37,321 at first serve).
  IT: 'scoped_financial_only',
  // Ireland: CRO open data has no turnover — capacity-anchored with
  // eTenders/NTPF actuals (268 at first serve).
  IE: 'scoped_financial_only',
  // Wave-2 remainder (registry-served 2026-07-09):
  // Spain: no free financials — fully capacity-anchored (beds/facilities/
  // places per the pinned serve contract; PLACSP actuals wired, 0 staged).
  ES: 'scoped_financial_only',
  // Czechia: justice.cz financials imputed-only at first serve — revenue
  // fully modelled (nrhzs_volume+imputed never lands as revenue per ruling).
  CZ: 'scoped_financial_only',
  // Switzerland: no filing obligation — KZP/SOMED reported statistics for
  // 862 institutions, the rest floor-served.
  CH: 'scoped_financial_only',
  // Poland: served 2026-07-10 reported-only (e-sprawozdania RDF) — the
  // v0-seed model gates all modelled cells pending until the NFZ refit.
  PL: 'scoped_financial_only',
  // Wave-3A (registry-served 2026-07-10):
  // Greece: GEMI publicity-portal filings parse free for ~13% of the
  // universe (units-scale cohort; the thousands-scale cohort is HELD on a
  // ×1000 defect) — the rest floor-serve.
  GR: 'scoped_financial_only',
  // Slovenia: no free bulk turnover (AJPES JOLP rate-capped) — fully
  // modelled on capacity/anchors; JOLP/ERAR actuals wired, 0 staged.
  SI: 'scoped_financial_only',
  // Lithuania: RC JAR_FA whole-population turnover = the spine (staged
  // pending its dump); non-reporting tail serves per-operator medians.
  LT: 'scoped_financial_only',
  // Croatia: FINA RGFI turnover free for the incorporated filer slice
  // (821 reported at first serve); obrt sole-traders + name-keyed tail
  // modelled (beds_places capacity + median anchors).
  HR: 'scoped_financial_only',
  // Hungary: NEAK reimbursement actuals = the reported anchor (every funded
  // operator P100); no free filed financials (NAV closed).
  HU: 'scoped_financial_only',
  // Austria: no broad free structured turnover (UGB §278 exemptions) —
  // residential capacity-anchored; HVD reporter base wired for later.
  AT: 'scoped_financial_only',
  // Portugal: surfaced at reach-full (2026-07-10) — the physio MCDT
  // designated anchor lifts 255 above the gate; residential/RT refits
  // follow.
  PT: 'scoped_financial_only',
  // Slovakia: RÚZ reported turnover covers 73% of the universe (P100);
  // SZČO sole-trader tail modelled-pending until the median re-stamp.
  SK: 'scoped_financial_only',
  // Bulgaria: outpatient universe capacity-anchored (pharmacy outlets +
  // diagnostics facilities serve); hospital layer = greenlit fast-follow;
  // CR-GFO reported thin (scanned/OCR).
  BG: 'scoped_financial_only',
};

export const SCOPE_FLAG_LABELS: Record<ScopeFlag, string> = {
  full: 'Full coverage',
  scoped_ixbrl_only: 'iXBRL filings only',
  scoped_financial_only: 'Financials only — identity resolution required',
  screener_only: 'Screener mode — identity and structural only',
};
